import { Box, Link, Tooltip } from "@mui/material";
import { DataGridProProps, GridRowId, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { Link as RouterLink } from "react-router-dom";
import zerezLogoIcon from "assets/icons/zerezLogoIcon.svg";
import StatusTag from "components/molecules/StatusTag/StatusTag";
import useDataGrid from "components/templates/dataGridTable";
import DetailPanelToggle from "components/templates/dataGridTable/Cells/DetailPanelToggle";
import { useGetUnitsForUnitsOverviewLazyQuery } from "graphqlBase/getUnitsForUnitsOverview/__generated__/getUnitsForUnitsOverview";
import { ReactComponent as NESSymbolIcon } from "components/icons/iconsSvg/NESSymbol.svg";

import ClampedText from "components/atomics/ClampedText";
import {
  getTranslatedCategoryEnumString,
  getTranslatedCertificateTypeString,
  getTranslatedPrimaryEnergySourceString,
} from "lib/helper/getTranslatedRowDataStrings";
import { useGetUserInfo } from "lib/userInfo";
import React, { useCallback, useEffect, useState } from "react";
import { atom, useRecoilState } from "recoil";
import ordinaryColors from "themes/ordinaryColors";
import translations from "translations";
import { formatDate } from "translations/formatter";
import useGetTenantNameById from "utils/useGetTenantNameById";
import useCheckIfFiltersAreApplied from "../CustomDataGridFilters/utils/useCheckIfFiltersAreApplied";
import { useCustomFilterbar } from "../CustomDataGridFilters/utils/useCustomFilterbar";
import DetailPanelContent from "./DetailPanelContent";
import { StyledTitle } from "./DetailView/styles";
import { MigrationNoteTooltip } from "./MigrationNoteTooltip";
import StatusCell from "./StatusColumn/StatusCell";
import SubscribeColumn from "./SubscribeColumn";
import UnitsAndCertificatesLink, { getUnitLinkInput, UnitOverview } from "./UnitsAndCertificatesLink";
import { zerezIdFontStyle } from "utils/zerezIdStyle";

export const tenantHasRoleManufacturerOrCertifcationAuthorityAtom = atom<boolean>({
  key: "tenantHasRoleManufacturerOrCertifcationAuthorityAtom",
  default: false,
});

// Mirrors the detail view formatting (formatUnitParameters): active power is shown
// with a single trailing digit.
const formatActivePower = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  return value.toFixed(1);
};

// Builds the display string for the "Max Active Power" column, covering a single
// value, an equal range, and a min–max range.
const getMaxActivePowerCell = (
  row: Partial<
    Pick<UnitOverview, "hasActivePowerRange" | "minActivePowerRange" | "maxActivePowerRange" | "maxActivePower">
  >
): string => {
  if (!row.hasActivePowerRange) return `${formatActivePower(row.maxActivePower)} kW`;

  const isRangeNull = row.minActivePowerRange === null && row.maxActivePowerRange === null;
  if (isRangeNull) return translations.entities.certificate.hasActivePowerRange;

  if (row.minActivePowerRange === row.maxActivePowerRange) return `${formatActivePower(row.minActivePowerRange)} kW`;

  return `${formatActivePower(row.minActivePowerRange)} - ${formatActivePower(row.maxActivePowerRange)} kW`;
};
interface UnitsOverviewProps {
  isUsedForUserManagement?: boolean;
  tenantId?: string;
}

const UnitsOverview: React.FC<UnitsOverviewProps> = ({ tenantId, isUsedForUserManagement }) => {
  const { filtersIsTrue } = useCheckIfFiltersAreApplied();
  const [noDataMessage, setNoDataMessage] = useState<string | undefined>(undefined);
  const userInfo = useGetUserInfo();

  const [queryPre, { data, loading, error, refetch }] = useGetUnitsForUnitsOverviewLazyQuery({
    variables: {
      isLoggedIn: !!userInfo,
      userId: userInfo?.id,
      take: 1000,
    },
    fetchPolicy: "network-only",
  });
  const { tenantNameById } = useGetTenantNameById({ withDeniedTenants: true });

  const userId = userInfo?.id;
  const currentTenantId = userInfo?.tenantId;
  const zerezLogo = <img src={zerezLogoIcon} alt="ZEREZ Logo" />;
  const query = useCustomFilterbar(queryPre);
  const refetchUnitsOverview = useCallback(async () => {
    if (refetch) await refetch();
  }, [refetch]);
  const { DataGrid, useMakeColumns, makeSeverSideFilter, apiRef } = useDataGrid<UnitOverview, "UnitOverview">({
    query,
    tableId: "UnitOverview",
    persistance: "runTime",
    variables: {
      where: {
        ...(isUsedForUserManagement
          ? { or: [{ certificateHolderId: { eq: tenantId } }, { certificateAuthorityId: { eq: tenantId } }] }
          : {}),
      },
    },
  });
  const minColumnWidth = 150;
  const [tenantHasRoleManufacturerOrCertifcationAuthority, setTenantHasRoleManufacturerOrCertifcationAuthority] =
    useRecoilState(tenantHasRoleManufacturerOrCertifcationAuthorityAtom);
  const isPlatformOperator = userInfo?.role === "PLATFORM_OPERATOR";
  const isCertificationBody = userInfo?.role === "CERTIFICATION_AUTHORITY";

  useEffect(() => {
    if (!userInfo?.role) return;

    setTenantHasRoleManufacturerOrCertifcationAuthority(
      userInfo.role === "MANUFACTURER" || userInfo.role === "CERTIFICATION_AUTHORITY"
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);
  const columns = useMakeColumns(
    [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        renderCell: (params: { id?: GridRowId; value?: unknown; row: UnitOverview }) => {
          // if (params.row.certificateBridgeUnits.length === 0) return <div />;
          return <DetailPanelToggle id={params.id ?? ""} value={params.value} />;
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            certificateNumber: filterValue,
          }),
        }),
        type: "string",
        // width: 200,
      },
      {
        field: "ModelName",
        headerName: translations.entities.unit.modelName,
        flex: 1,
        minWidth: 200,
        valueGetter: (params) => {
          return params.row.modelName ?? "";
        },

        renderCell: ({ row }) => {
          return (
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
              {row.isImported && <MigrationNoteTooltip entity="unit" />}
              <span
                style={{
                  display: "flex",
                  width: "fit-content",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: "-3px",
                  wordBreak: "break-word",
                }}
              >
                {UnitsAndCertificatesLink(
                  getUnitLinkInput({ row, hasActiveErrorReport: row.hasActiveErrorReport, isPlatformOperator })
                )}
              </span>
              {(row.hasMatrixParameters || row.matrixType != null || row.inertiaParameters != null) && (
                <Tooltip title={translations.pages.certificationManagement.gridFormingUnitTooltip}>
                  <NESSymbolIcon width="1.8rem" height="1.8rem" />
                </Tooltip>
              )}
            </Box>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            modelName: filterValue,
          }),
        }),
        type: "string",
      },

      {
        field: "UnitNumber",
        headerName: translations.entities.unit.unitNumber,
        flex: 1,
        minWidth: minColumnWidth,

        // valueGetter: (params) => {
        //   return params.row.unitCode ?? "";
        // },
        renderCell: ({ row }) => {
          return (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {" "}
              {row.isImported && !row.isVerified ? (
                "-"
              ) : row.hasActivePowerRange &&
                !row.withTG8Rev25Conformity &&
                (row.minActivePowerRange !== row.maxActivePowerRange ||
                  (row.minActivePowerRange === null && row.maxActivePowerRange === null)) ? (
                UnitsAndCertificatesLink({
                  label: translations.pages.certificationManagement.referToDetailView,
                  path: `/units/${row?.id}`,
                })
              ) : row.withTG8Rev25Conformity ? (
                translations.globals.helpText.notAssigned
              ) : (
                <span style={zerezIdFontStyle}>{row.unitCode}</span>
              )}
            </span>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            unitCode: filterValue,
          }),
        }),
        type: "string",
      },
      isCertificationBody
        ? {
            field: "CertificateNumber",
            headerName: translations.entities.certificate.certificateNumber,
            type: "string",
            flex: 2,
            minWidth: 150,
            renderCell: ({ row }) => {
              return (
                <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      marginTop: "-3px",
                      wordBreak: "break-word",
                    }}
                  >
                    {UnitsAndCertificatesLink({
                      label: row.certificateNumber ?? "",
                      path: `/certificates/${row?.certificateId}`,
                    })}
                  </span>
                  {row.isImported && <MigrationNoteTooltip entity="certificate" />}
                </Box>
              );
            },
          }
        : {
            field: "UnitType",
            headerName: translations.entities.unit.unitTypeName,
            flex: 1,
            minWidth: minColumnWidth,

            valueGetter: ({ row }) => getTranslatedCategoryEnumString(row.category),

            type: "string",
          },
      {
        field: "Norm",
        headerName: translations.entities.certificate.certificateNorm,
        flex: 1,
        minWidth: minColumnWidth,

        renderCell: ({ row }) => {
          return (
            <div style={{ display: "flex" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.certificateNormIssueDateDescriptions}
              </span>
            </div>
          );
        },

        type: "string",
      },
      isCertificationBody
        ? {
            field: "certificateIssueDate",
            headerName: translations.entities.certificate.certificateIssueDate,
            flex: 1,
            minWidth: minColumnWidth,
            valueGetter: (params) =>
              params.row.certificateIssueDate
                ? formatDate(new Date(params.row.certificateIssueDate))
                : translations.globals.helpText.notAssigned,
            type: "string",
          }
        : {
            field: "PrimaryEnergy",
            headerName: translations.entities.certificate.primaryEnergy,
            flex: 1,
            minWidth: minColumnWidth,
            valueGetter: (params) => getTranslatedPrimaryEnergySourceString(params.row.primaryEnergySource),
            type: "string",
          },
      {
        field: "Manufacturer",
        headerName: translations.entities.certificate.manufacturer,
        flex: 1,
        minWidth: minColumnWidth,
        valueGetter: (params) =>
          params.row.certificateHolderName ?? tenantNameById[params.row.certificateHolderId as string] ?? "",
        renderCell: ({ row }) => {
          const name = row.certificateHolderName ?? tenantNameById[row.certificateHolderId as string] ?? "";
          return <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>;
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ filterValue }) => ({
            certificateHolderName: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "MaxActivePower",
        headerName: translations.entities.certificate.maxActivePower,
        flex: 1,
        minWidth: minColumnWidth,
        // TODO: Filter adjustment for power range
        valueGetter: (params) => getMaxActivePowerCell(params.row),
        type: "string",
      },
      {
        field: "RatedVoltage",
        headerName: translations.entities.certificate.ratedVoltage,
        flex: 1,
        minWidth: minColumnWidth,
        valueGetter: (params) => {
          return `${params.row.ratedVoltage?.toString() ?? ""} V`;
        },
        type: "string",
      },
      {
        field: "CertificateTypeName",
        headerName: translations.entities.certificate.certificateType,
        minWidth: minColumnWidth,

        valueGetter: (params) => getTranslatedCertificateTypeString(params.row.certificateTypeId),

        type: "string",
      },
      {
        field: "Status",
        headerName: translations.pages.certificationManagement.certificateStatus,
        minWidth: minColumnWidth,

        renderCell: ({ row }) => {
          const certificate = row;
          const statusId = certificate?.certificateValidityStatusId;
          const statusName = certificate?.certificateValidityStatusName;

          // Handle replaced certificate info
          const replacedByCertificateId = certificate?.replacedByCertificate?.id;
          const replacedByCertificateNumber = certificate?.replacedByCertificate?.certificateNumber;

          // Handle extended certificate info
          const extendedByCertificateId = certificate?.parentCertificateForValidityExtension?.id;
          const extendedByCertificateNumber = certificate?.parentCertificateForValidityExtension?.certificateNumber;
          // Only override to "Valid (Extended By)" if the current status is actually "Valid" and there's extension info
          // Don't override if the certificate has been withdrawn or has another explicit status
          const adjustedStatus =
            statusId === "c47a0b7c-701b-5544-92b0-6bcb48aac633" &&
            extendedByCertificateId &&
            extendedByCertificateNumber
              ? "Valid (Extended By)"
              : statusName;

          return (
            <StatusCell
              status={adjustedStatus}
              replacedByCertificate={
                replacedByCertificateId && replacedByCertificateNumber
                  ? {
                      certificateNumber: replacedByCertificateNumber,
                      id: replacedByCertificateId,
                    }
                  : undefined
              }
              extendedByCertificate={
                extendedByCertificateId && extendedByCertificateNumber
                  ? {
                      certificateNumber: extendedByCertificateNumber,
                      id: extendedByCertificateId,
                    }
                  : undefined
              }
            />
          );
        },

        type: "string",
      },
      {
        field: "Certificate Check",
        headerName: translations.pages.certificationManagement.certificateCheck,
        minWidth: minColumnWidth,

        renderCell: ({ row }) => {
          const inCorrection = row.hasActiveErrorCorrection;
          const isValidityStatusVoid = row.certificateValidityStatusName === "Void";
          const isVerified = !!row.isVerified;
          if (isValidityStatusVoid) return null;
          if (inCorrection)
            return (
              <StatusTag
                title={translations.pages.certificationManagement.inCorrection}
                color={ordinaryColors.warningDark}
              />
            );
          return isVerified ? (
            <StatusTag
              title={translations.pages.certificationManagement.verified}
              backgroundColor={ordinaryColors.sucess200}
              color={ordinaryColors.grey900}
              borderColor="transparent"
            />
          ) : (
            <StatusTag
              title={translations.pages.certificationManagement.verificationPending}
              color={ordinaryColors.grey600}
            />
          );
        },

        type: "string",
      },
      {
        field: "SubscribeCheck",
        headerName: translations.pages.certificationManagement.subscribeStar,
        // flex: 2,
        minWidth: minColumnWidth,

        renderCell: ({ row }) => {
          const unitBridgeUserId = (data?.unitBridgeUsers?.items ?? [])?.find((bridge) => bridge.unitId === row.id)?.id;

          const unitId = row?.id;
          const unitTenantIdEqualsCurrentTenantId =
            row?.certificateHolderId === currentTenantId || row?.certificateAuthorityId === currentTenantId;

          return userId !== undefined ? (
            <SubscribeColumn
              unitTenantIdEqualsCurrentTenantId={unitTenantIdEqualsCurrentTenantId}
              unitId={unitId}
              userId={userId}
              isSubscribed={!!unitBridgeUserId}
              unitBridgeUserId={unitBridgeUserId ?? ""}
              refetchUnitsOverview={refetchUnitsOverview}
            />
          ) : null;
        },
        type: "string",
        hide: userId === undefined,
      },
    ],
    []
  );
  const columnsforUserManagement = useMakeColumns(
    [
      {
        field: "ModelName",
        headerName: translations.entities.unit.modelName,
        flex: 1,
        valueGetter: (params) => {
          return params.row.modelName ?? "";
        },
        renderCell: ({ row }) => {
          return (
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              {row.isImported && <MigrationNoteTooltip entity="unit" />}
              <span
                style={{
                  display: "flex",
                  width: "fit-content",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  marginTop: "-3px",
                  wordBreak: "break-word",
                }}
              >
                {UnitsAndCertificatesLink(
                  getUnitLinkInput({ row, hasActiveErrorReport: row.hasActiveErrorReport, isPlatformOperator })
                )}
              </span>
            </Box>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            modelName: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "CertificateNumber",
        headerName: translations.entities.certificate.certificateNumber,
        type: "string",
        flex: 1,
        remoteOrder: (sort) => ({ certificateNumber: sort }),
        renderCell: ({ row }) => {
          const certPath = `/certificates/${row.certificateId}`;
          return (
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <Link
                component={RouterLink}
                to={certPath}
                href={certPath}
                sx={{
                  cursor: "pointer",
                  paddingTop: "5px",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                underline="none"
              >
                <span style={zerezIdFontStyle}>
                  <ClampedText>{row.certificateNumber}</ClampedText>
                </span>
              </Link>
              {row.isImported && <MigrationNoteTooltip entity="certificate" />}
            </Box>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            certificateNumber: filterValue,
          }),
        }),
      },
      {
        field: "UnitNumber",
        headerName: translations.entities.unit.unitNumber,
        flex: 1,
        minWidth: minColumnWidth,
        renderCell: ({ row }) => {
          return (
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {" "}
              {row.isImported && !row.isVerified ? (
                "-"
              ) : row.hasActivePowerRange &&
                (row.minActivePowerRange !== row.maxActivePowerRange ||
                  (row.minActivePowerRange === null && row.maxActivePowerRange === null)) ? (
                UnitsAndCertificatesLink({
                  label: translations.pages.certificationManagement.referToDetailView,
                  path: `/units/${row?.id}`,
                })
              ) : (
                <span style={zerezIdFontStyle}>{row.unitCode}</span>
              )}
            </span>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            unitCode: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "norm",
        headerName: translations.entities.certificate.certificateNorm,
        flex: 2,
        minWidth: minColumnWidth,
        renderCell: ({ row }) => {
          return (
            <div style={{ display: "flex" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                {row.certificateNormIssueDateDescriptions}
              </span>
            </div>
          );
        },
        remoteFilter: makeSeverSideFilter("string", {
          filterPath: ({ where, filterValue }) => ({
            certificateNormIssueDateDescriptions: filterValue,
          }),
        }),
        type: "string",
      },
      {
        field: "Status",
        headerName: translations.pages.certificationManagement.certificateStatus,
        flex: 1,

        renderCell: ({ row }) => {
          const status = row.certificateValidityStatusName;
          return (
            <Box>
              <StatusCell status={status} />
            </Box>
          );
        },

        type: "string",
      },
      {
        field: "Certificate Check",
        headerName: translations.pages.certificationManagement.certificateCheck,
        flex: 1,
        renderCell: ({ row }) => {
          const inCorrection = row.hasActiveErrorCorrection;
          const isValidityStatusVoid = row.certificateValidityStatusName === "Void";
          const isVerified = !!row.isVerified;
          if (isValidityStatusVoid) return null;
          if (inCorrection)
            return (
              <StatusTag
                title={translations.pages.certificationManagement.inCorrection}
                color={ordinaryColors.warningDark}
              />
            );
          return isVerified ? (
            <StatusTag
              title={translations.pages.certificationManagement.verified}
              backgroundColor={ordinaryColors.sucess200}
              color={ordinaryColors.grey900}
              borderColor="transparent"
            />
          ) : (
            <StatusTag
              title={translations.pages.certificationManagement.verificationPending}
              color={ordinaryColors.grey600}
            />
          );
        },
        type: "string",
      },
    ],
    []
  );

  const getDetailPanelContent = useCallback<NonNullable<DataGridProProps["getDetailPanelContent"]>>(
    ({ row }) => {
      if (row.certificate) return null;
      return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DetailPanelContent row={row} isPlatformOperator={isPlatformOperator} />
        </Box>
      );
    },
    [isPlatformOperator]
  );
  const refetchOnClick = useCallback(() => {
    refetch();
  }, [refetch]);
  const getDetailPanelHeight = useCallback<NonNullable<DataGridProProps["getDetailPanelHeight"]>>(
    () => "auto" as const,
    []
  );

  useEffect(() => {
    if (filtersIsTrue) {
      setNoDataMessage(undefined);
      return;
    }
    setTimeout(() => {
      if (data?.unitOverview?.totalCount === 0) setNoDataMessage(translations.globals.notifications.noDataAvailable);
    }, 200);
  }, [data?.unitOverview?.totalCount, filtersIsTrue]);

  return (
    <>
      {isUsedForUserManagement ? null : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            gap: "15px",
            marginLeft: "30px",
            padding: "28px 0 28px",
          }}
        >
          <Box sx={{ width: "64px", height: "64px" }}>{zerezLogo}</Box>

          <StyledTitle component="h1" role="region">
            {" "}
            {translations.pages.certificationManagement.title}
          </StyledTitle>
        </Box>
      )}
      <Box role="region" style={{ display: "flex", flexDirection: "column", minHeight: "auto", maxHeight: "auto" }}>
        <DataGrid
          showReducedFunctionalityNote={tenantHasRoleManufacturerOrCertifcationAuthority}
          showMigrationNote={false}
          componentsProps={{ pagination: { labelRowsPerPage: translations.globals.table.pagination.rowsPerPage } }}
          loading={loading}
          sx={{
            "& .MuiDataGrid-cell": {
              maxWidth: "200px",
              width: "200px",
              alignItems: "start",
              diplay: "flex",
            },
            "& .MuiDataGrid-main": {
              minHeight: "100vh",
              height: "auto",
            },
          }}
          initialState={{
            pinnedColumns: {
              left: !isUsedForUserManagement ? [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field, "ModelName"] : [],
            },
          }}
          disableColumnFilter={!isUsedForUserManagement}
          disableSelectionOnClick
          error={error}
          hasToolbar
          columns={isUsedForUserManagement ? columnsforUserManagement : columns}
          noResultsAnimation
          rows={data?.unitOverview?.items}
          noDataMessage={noDataMessage}
          autoHeight={false}
          tableHeight="auto"
          tableTitle="Assets"
          apiRef={apiRef}
          totalCount={data?.unitOverview?.totalCount}
          checkboxSelection={false}
          getDetailPanelContent={isUsedForUserManagement ? undefined : getDetailPanelContent}
          pageSize={20}
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
          getDetailPanelHeight={isUsedForUserManagement ? undefined : getDetailPanelHeight}
          pagination
          withMargin={isUsedForUserManagement ? false : true}
          rowHeight={200}
          getRowHeight={() => "auto"}
          customToolBarProps={
            isUsedForUserManagement
              ? undefined
              : {
                  isUnitsToolbar: true,
                  refetchOnClick,
                  hasCustomFilterbar: true,
                  showReducedFunctionalityNote: tenantHasRoleManufacturerOrCertifcationAuthority,
                }
          }
        />
      </Box>
    </>
  );
};

export default UnitsOverview;
